/**
 * Menu-component registry — stub form.
 *
 * <p>ThemeMaker's full {@code menuComponents.tsx} (~1.3K LOC) ships
 * a catalog of pre-built menu inserts (categoriesMenu, subscriptions
 * list, live-event countdown, etc.) that operators can attach as
 * {@code kind: 'component'} menu entries.
 *
 * <p>The stub here returns {@code null} for every componentId — the
 * Headers renderer treats that as "registered component is missing,
 * skip this entry". Tenants whose shell configs don't use
 * {@code kind: 'component'} (the common case, including the current
 * BTK config) see no behaviour difference. Hosts that need the full
 * catalog wire in real components via {@link registerMenuComponent}.
 *
 * <p>Full port — when needed — should expand this module with each
 * concrete component (CategoriesMenu, SubscriptionsList, etc.) as its
 * own {@code 'use client'} file under {@code src/menu/components/}.
 */

import type { ComponentType, ReactNode } from 'react';
import type { CurrentUser, LocalizedString } from '../../portal';

/** Props every menu component receives. Defined here so hosts that
 *  register their own components match the contract Headers expects. */
export interface MenuComponentProps {
  /** Where this entry is rendering — header top-bar, sidebar rail,
   *  or mobile drawer. Components can render differently per surface
   *  (compact horizontal vs vertical-with-label vs etc.). */
  placement: 'header-horizontal' | 'sidebar' | 'mobile-drawer';
  /** Collapsed-sidebar mode — only meaningful when
   *  {@code placement === 'sidebar'}. */
  collapsed: boolean;
  tenantSlug: string;
  locale: string;
  fallbackLocale: string;
  currentUser: CurrentUser | null;
  /** Merged props: operator-set {@code props} + mirrored sibling
   *  fields ({@code label}, {@code allCategoriesLabel},
   *  {@code showCategoryIcons}). */
  props: Record<string, unknown> & {
    label?: LocalizedString;
    allCategoriesLabel?: LocalizedString;
    showCategoryIcons?: boolean;
  };
}

export type MenuComponent = ComponentType<MenuComponentProps>;

const REGISTRY: Record<string, MenuComponent> = {};

/** Host-side registration. Wires a {@code kind: 'component'} entry's
 *  {@code componentId} to a real React component. */
export function registerMenuComponent(id: string, component: MenuComponent): void {
  REGISTRY[id] = component;
}

/** Headers + sidebar lookup. Returns undefined for unregistered ids —
 *  the renderer skips the entry rather than crashing. */
export function getMenuComponent(id: string): MenuComponent | undefined {
  return REGISTRY[id];
}

/** Diagnostic helper for tests and admin tooling — sorted list of
 *  every registered component id. */
export function listMenuComponents(): string[] {
  return Object.keys(REGISTRY).sort();
}

/** Stub component the Headers MobileMenu uses for `kind: 'component'`
 *  entries when no real component is registered. Renders nothing —
 *  callers should branch on {@code getMenuComponent(id)} returning
 *  undefined before invoking this; it exists as a safe last-resort. */
export function NullMenuComponent(_props: MenuComponentProps): ReactNode {
  return null;
}
